/* eslint-disable max-len */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Row, Col, Form } from 'react-bootstrap'
import CustomColorSelect from '@components/layout/Modals/AddProviderModal/CustomColorSelect.component'
import ProfessionalIdInput from '@components/common/ProfessionalIdInput/ProfessionalIdInput.component'
import ExpiryDateInput from '@components/common/ExpiryDateInput/ExpiryDateInput.component'
import { ProviderData } from '@dataTypes/provider.types'
import {
  EmploymentStatus,
  NpiType,
  ProviderWorkPosition,
  TaxIdType,
  US_STATE_OPTIONS,
  WorkStatus,
  isProviderSeatRole,
} from '@constants/Providers'
import {
  isProviderSeatRoleOptionDisabled,
  shouldRestrictProviderSeatRoles,
} from '@utils/providerRole.util'
import { createEmptyProviderData } from '@utils/provider.mapper'
import { useAccountsHeader } from '@pages/Accounts/AccountsHeader.context'
import { useProfile } from '@pages/Accounts/AccountsProfile.context'
import { AccountsSection } from '@constants/Accounts'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import AccountsHeaderActionButton from './AccountsHeaderActionButton.component'
import { areValuesEqual } from '@utils/settings.util'
import './ProfessionalInfo.component.css'
import { getAllStaffs, getProviderSeatLimit } from '@services/api/provider.api'

type ProfessionalFormState = Pick<
  ProviderData,
  | 'stateLicenseNumber'
  | 'stateWhereLicensed'
  | 'expiryDate'
  | 'stateRxId'
  | 'deaNumber'
  | 'taxIdType'
  | 'taxIdNumber'
  | 'socialSecurityNumber'
  | 'npiType'
  | 'npiNumber'
  | 'workRole'
  | 'employmentType'
  | 'providerColor'
>

const buildInitialForm = (): ProfessionalFormState => {
  const base = createEmptyProviderData()
  return {
    stateLicenseNumber: base.stateLicenseNumber ?? '',
    stateWhereLicensed: base.stateWhereLicensed ?? '',
    expiryDate: base.expiryDate ?? '',
    stateRxId: base.stateRxId ?? '',
    deaNumber: base.deaNumber ?? '',
    taxIdType: base.taxIdType ?? '',
    taxIdNumber: base.taxIdNumber ?? '',
    socialSecurityNumber: base.socialSecurityNumber ?? '',
    npiType: base.npiType ?? '',
    npiNumber: base.npiNumber ?? '',
    workRole: base.workRole ?? '',
    employmentType: base.employmentType ?? '',
    providerColor: base.providerColor ?? {
      header: '#D8352A',
      background: '#FFCBC8',
      text_color: '#470C08',
      border_color: 'var(--red-400)',
      label: 'Red',
    },
  }
}

const professionalToCompareValues = (
  data: ProfessionalFormState,
): (string | boolean | number)[] => [
  data.stateLicenseNumber ?? '',
  data.stateWhereLicensed ?? '',
  data.expiryDate ?? '',
  data.stateRxId ?? '',
  data.deaNumber ?? '',
  data.taxIdType ?? '',
  data.taxIdNumber ?? '',
  data.socialSecurityNumber ?? '',
  data.npiType ?? '',
  data.npiNumber ?? '',
  data.workRole ?? '',
  data.employmentType ?? '',
  JSON.stringify(data.providerColor ?? {}),
]

const profileToProfessionalForm = (
  profile: ProviderData,
): ProfessionalFormState => ({
  stateLicenseNumber: profile.stateLicenseNumber ?? '',
  stateWhereLicensed: profile.stateWhereLicensed ?? '',
  expiryDate: profile.expiryDate ?? '',
  stateRxId: profile.stateRxId ?? '',
  deaNumber: profile.deaNumber ?? '',
  taxIdType: profile.taxIdType ?? '',
  taxIdNumber: profile.taxIdNumber ?? '',
  socialSecurityNumber: profile.socialSecurityNumber ?? '',
  npiType: profile.npiType ?? '',
  npiNumber: profile.npiNumber ?? '',
  workRole: profile.workRole ?? '',
  employmentType: profile.employmentType ?? '',
  providerColor: profile.providerColor ?? buildInitialForm().providerColor,
})

const ProfessionalInfo: React.FC = () => {
  const { setHeaderButtons } = useAccountsHeader()
  const { profile, savePatch, loadState } = useProfile()
  const { showToast } = useNotificationToast()

  const [form, setForm] = useState<ProfessionalFormState>(buildInitialForm)

  const [providerSeatLimit, setProviderSeatLimit] = useState<number | null>(
    null,
  )
  const [activeProviderCount, setActiveProviderCount] = useState<number | null>(
    null,
  )

  useEffect(() => {
    let cancelled = false
    void Promise.all([getProviderSeatLimit(), getAllStaffs()]).then(
      ([limitRes, staffsRes]) => {
        if (cancelled) return

        if (
          limitRes.statusCode === 200 &&
          limitRes.data &&
          typeof limitRes.data.provider_limit === 'number'
        ) {
          setProviderSeatLimit(limitRes.data.provider_limit)
        } else {
          setProviderSeatLimit(null)
        }

        if (staffsRes.statusCode === 200 && staffsRes.data?.staffs) {
          const next = staffsRes.data.staffs.reduce((acc, s) => {
            const isActive = (s.work_status || '') === WorkStatus.ACTIVE
            if (!isActive) return acc
            return acc + (isProviderSeatRole(s.work_role) ? 1 : 0)
          }, 0)
          setActiveProviderCount(next)
        } else {
          setActiveProviderCount(null)
        }
      },
    )
    return () => {
      cancelled = true
    }
  }, [])

  const atProviderSeatLimit = useMemo(() => {
    if (providerSeatLimit == null || activeProviderCount == null) return false
    return activeProviderCount >= providerSeatLimit
  }, [activeProviderCount, providerSeatLimit])

  const originalWorkRole = profile?.workRole

  useEffect(() => {
    if (!profile) return
    setForm({
      stateLicenseNumber: profile.stateLicenseNumber ?? '',
      stateWhereLicensed: profile.stateWhereLicensed ?? '',
      expiryDate: profile.expiryDate ?? '',
      stateRxId: profile.stateRxId ?? '',
      deaNumber: profile.deaNumber ?? '',
      taxIdType: profile.taxIdType ?? '',
      taxIdNumber: profile.taxIdNumber ?? '',
      socialSecurityNumber: profile.socialSecurityNumber ?? '',
      npiType: profile.npiType ?? '',
      npiNumber: profile.npiNumber ?? '',
      workRole: profile.workRole ?? '',
      employmentType: profile.employmentType ?? '',
      providerColor: profile.providerColor ?? buildInitialForm().providerColor,
    })
  }, [profile])

  const handleSave = useCallback(async (): Promise<void> => {
    const ok = await savePatch(AccountsSection.Professional, {
      stateLicenseNumber: form.stateLicenseNumber,
      stateWhereLicensed: form.stateWhereLicensed,
      expiryDate: form.expiryDate,
      stateRxId: form.stateRxId,
      deaNumber: form.deaNumber,
      taxIdType: form.taxIdType,
      taxIdNumber: form.taxIdNumber,
      socialSecurityNumber: form.socialSecurityNumber,
      npiType: form.npiType,
      npiNumber: form.npiNumber,
      workRole: form.workRole,
      employmentType: form.employmentType,
      providerColor: form.providerColor,
    })
    showToast?.(
      ok
        ? createSuccessToast('Saved', 'Professional info updated.')
        : createFailedToast('Error', 'Could not save professional info.'),
    )
  }, [form, savePatch, showToast])

  const handleCancel = useCallback((): void => {
    if (profile) {
      setForm(profileToProfessionalForm(profile))
    }
  }, [profile])

  const hasChanges = useMemo(() => {
    if (!profile) return false
    return !areValuesEqual(
      professionalToCompareValues(form),
      professionalToCompareValues(profileToProfessionalForm(profile)),
    )
  }, [form, profile])

  const actionsDisabled = loadState !== 'ready' || !profile

  useEffect(() => {
    setHeaderButtons(
      <AccountsHeaderActionButton
        onClick={() => {
          if (actionsDisabled || !hasChanges) return
          void handleSave()
        }}
        onCancel={handleCancel}
        cancelDisabled={!hasChanges || actionsDisabled}
        saveDisabled={!hasChanges || actionsDisabled}
      >
        Save changes
      </AccountsHeaderActionButton>,
    )
    return () => setHeaderButtons(null)
  }, [setHeaderButtons, handleSave, handleCancel, hasChanges, actionsDisabled])

  const handleInputChange = useCallback(
    (field: keyof ProfessionalFormState, value: string): void => {
      setForm((prev) => ({ ...prev, [field]: value }))
    },
    [],
  )

  const handleProviderColorChange = useCallback(
    (color: ProfessionalFormState['providerColor']): void => {
      setForm((prev) => ({ ...prev, providerColor: color }))
    },
    [],
  )

  return (
    <div className="accounts-professional-info">
      <div className="work-info-numbers">
        <Row className="mb-2 gx-2">
          <Col md={6}>
            <ProfessionalIdInput
              id="accounts-prof-state-license"
              type="STATE_LICENSE"
              label="State license number"
              value={form.stateLicenseNumber || ''}
              onChange={(value) =>
                handleInputChange('stateLicenseNumber', value)
              }
            />
          </Col>
          <Col md={4}>
            <Form.Group controlId="accounts-prof-state-where-licensed">
              <Form.Label>State</Form.Label>
              <Form.Select
                value={form.stateWhereLicensed || ''}
                onChange={(e) =>
                  handleInputChange('stateWhereLicensed', e.target.value)
                }
              >
                <option value="">Select state</option>
                {US_STATE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <ExpiryDateInput
              id="accounts-prof-expiry"
              label="Expiry date"
              value={form.expiryDate || ''}
              onChange={(value) => handleInputChange('expiryDate', value)}
              simpleError={true}
            />
          </Col>
        </Row>
        <Row className="mb-2 gx-2">
          <Col md={6}>
            <ProfessionalIdInput
              id="accounts-prof-state-rx"
              type="STATE_RX_ID"
              label="State Rx ID"
              value={form.stateRxId || ''}
              onChange={(value) => handleInputChange('stateRxId', value)}
            />
          </Col>
          <Col md={6}>
            <ProfessionalIdInput
              id="accounts-prof-dea"
              type="DEA"
              label="DEA number"
              value={form.deaNumber || ''}
              onChange={(value) => handleInputChange('deaNumber', value)}
            />
          </Col>
        </Row>
        <Row className="mb-2 gx-2">
          <Col md={6}>
            <Form.Group controlId="accounts-prof-tax-id-type">
              <Form.Label>TIN or SSN</Form.Label>
              <Form.Select
                value={form.taxIdType || ''}
                onChange={(e) => handleInputChange('taxIdType', e.target.value)}
              >
                <option value="">Select</option>
                {Object.values(TaxIdType).map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            {form.taxIdType === 'TIN' ? (
              <ProfessionalIdInput
                id="accounts-prof-tax-id"
                type="TAX_ID"
                label="Tax ID number"
                value={form.taxIdNumber || ''}
                onChange={(value) => handleInputChange('taxIdNumber', value)}
              />
            ) : form.taxIdType === 'SSN' ? (
              <ProfessionalIdInput
                id="accounts-prof-ssn"
                type="SSN"
                label="Social security number"
                value={form.socialSecurityNumber || ''}
                onChange={(value) =>
                  handleInputChange('socialSecurityNumber', value)
                }
              />
            ) : null}
          </Col>
        </Row>
        <Row className="mb-2 gx-2">
          <Col md={6}>
            <Form.Group controlId="accounts-prof-npi-type">
              <Form.Label>NPI type</Form.Label>
              <Form.Select
                value={form.npiType || ''}
                onChange={(e) => handleInputChange('npiType', e.target.value)}
              >
                <option value="">Select</option>
                {Object.values(NpiType).map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <ProfessionalIdInput
              id="accounts-prof-npi-number"
              type="NPI"
              label="NPI number"
              value={form.npiNumber || ''}
              onChange={(value) => handleInputChange('npiNumber', value)}
            />
          </Col>
        </Row>
      </div>

      <Row className="mb-2 gx-2">
        <Col md={12}>
          <Form.Group controlId="accounts-prof-work-role">
            <Form.Label>Work role</Form.Label>
            <Form.Select
              value={form.workRole || ''}
              onChange={(e) => handleInputChange('workRole', e.target.value)}
            >
              <option value="">Select</option>
              {Object.values(ProviderWorkPosition).map((value) => (
                <option
                  key={value}
                  value={value}
                  disabled={isProviderSeatRoleOptionDisabled(
                    atProviderSeatLimit,
                    originalWorkRole,
                    value,
                  )}
                >
                  {value}
                </option>
              ))}
            </Form.Select>
            {shouldRestrictProviderSeatRoles(
              atProviderSeatLimit,
              originalWorkRole,
            ) && (
              <div className="small text-danger mt-2" role="status">
                <strong>Note:</strong> Provider limit reached. Kindly contact
                your superadmin.
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 gx-2 align-items-start">
        <Col md={6}>
          <Form.Group controlId="accounts-prof-work-position">
            <Form.Label>Work position</Form.Label>
            <Form.Select
              value={form.employmentType || ''}
              onChange={(e) =>
                handleInputChange('employmentType', e.target.value)
              }
            >
              <option value="">Select</option>
              {Object.values(EmploymentStatus).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6} className="accounts-professional-color-col">
          <Form.Group controlId="accounts-prof-staff-color">
            <Form.Label>Staff color</Form.Label>
            <CustomColorSelect
              value={form.providerColor}
              onChange={handleProviderColorChange}
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  )
}

export default ProfessionalInfo
